import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';

const AdminSystemHealth = () => {
    const { theme } = useTheme();
    const { getToken } = useAuth();
    const [systemHealth, setSystemHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        fetchSystemHealth();
        // Set up auto-refresh every 30 seconds
        const interval = setInterval(fetchSystemHealth, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchSystemHealth = async () => {
        try {
            setError(null);
            const token = await getToken();
            const response = await fetch('https://parrotmate.com/api/admin/system-health', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSystemHealth(data);
                setLastUpdated(new Date());
            } else {
                throw new Error('Failed to fetch system health data');
            }
        } catch (error) {
            console.error('Failed to fetch system health:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const HealthMetricCard = ({ title, status, value, unit, icon, details, threshold, alerts = [] }) => {
        const getStatusColor = (status) => {
            switch (status) {
                case 'healthy': return 'text-green-600';
                case 'warning': return 'text-yellow-600';
                case 'critical': return 'text-red-600';
                default: return theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
            }
        };

        const getStatusBg = (status) => {
            switch (status) {
                case 'healthy': return 'bg-green-100 border-green-200';
                case 'warning': return 'bg-yellow-100 border-yellow-200';
                case 'critical': return 'bg-red-100 border-red-200';
                default: return theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
            }
        };

        const getStatusIcon = (status) => {
            switch (status) {
                case 'healthy': return '✅';
                case 'warning': return '⚠️';
                case 'critical': return '🚨';
                default: return '❓';
            }
        };

        return (
            <div className={`${theme === 'dark' && status === 'healthy'
                ? 'bg-gray-800 border-gray-700'
                : getStatusBg(status)
                } rounded-lg shadow border p-6 relative`}>
                {/* Warning indicator for critical/warning status */}
                {(status === 'critical' || status === 'warning') && (
                    <div className="absolute top-2 right-2">
                        <div className={`w-3 h-3 rounded-full ${status === 'critical' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
                            }`}></div>
                    </div>
                )}

                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                            <span className="text-2xl">{icon}</span>
                            <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                {title}
                            </h3>
                        </div>

                        <div className="flex items-center space-x-2 mb-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status === 'healthy' ? 'bg-green-600 text-white' :
                                status === 'warning' ? 'bg-yellow-600 text-white' :
                                    status === 'critical' ? 'bg-red-600 text-white' :
                                        'bg-gray-600 text-white'
                                }`}>
                                {getStatusIcon(status)} {status?.toUpperCase() || 'UNKNOWN'}
                            </span>
                            {value !== undefined && (
                                <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                    {value}{unit}
                                </span>
                            )}
                        </div>

                        {details && (
                            <div className="space-y-1">
                                {details.map((detail, index) => (
                                    <div key={index} className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                        }`}>
                                        <span className="font-medium">{detail.label}:</span> {detail.value}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Component-specific alerts */}
                        {alerts.length > 0 && (
                            <div className="mt-3 space-y-1">
                                {alerts.slice(0, 2).map((alert, index) => (
                                    <div key={index} className={`text-xs p-2 rounded ${alert.type === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200' :
                                        alert.type === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200' :
                                            'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                                        }`}>
                                        {alert.message}
                                    </div>
                                ))}
                                {alerts.length > 2 && (
                                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                        +{alerts.length - 2} more alerts
                                    </div>
                                )}
                            </div>
                        )}

                        {threshold && (
                            <div className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                Threshold: {threshold}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const DatabaseHealthCard = () => {
        if (!systemHealth?.database) return null;

        const db = systemHealth.database;
        const storagePercent = db.storageLimit > 0 ? (db.storageUsed / db.storageLimit) * 100 : 0;
        const connectionPercent = db.maxConnections > 0 ? (db.connectionCount / db.maxConnections) * 100 : 0;

        // Generate component-specific alerts
        const dbAlerts = [];
        if (storagePercent > 80) {
            dbAlerts.push({
                type: storagePercent > 90 ? 'critical' : 'warning',
                message: `Storage usage at ${storagePercent.toFixed(1)}%`
            });
        }
        if (db.queryPerformance.errorRate > 1) {
            dbAlerts.push({
                type: db.queryPerformance.errorRate > 5 ? 'critical' : 'warning',
                message: `High error rate: ${db.queryPerformance.errorRate.toFixed(2)}%`
            });
        }
        if (db.queryPerformance.averageResponseTime > 100) {
            dbAlerts.push({
                type: 'warning',
                message: `Slow queries: ${db.queryPerformance.averageResponseTime.toFixed(1)}ms avg`
            });
        }

        return (
            <HealthMetricCard
                title="Database Health"
                status={db.status}
                icon="🗄️"
                details={[
                    { label: 'Storage Used', value: `${db.storageUsed.toFixed(2)} GB / ${db.storageLimit} GB (${storagePercent.toFixed(1)}%)` },
                    { label: 'Active Connections', value: `${db.connectionCount} / ${db.maxConnections} (${connectionPercent.toFixed(1)}%)` },
                    { label: 'Avg Query Time', value: `${db.queryPerformance.averageResponseTime.toFixed(1)} ms` },
                    { label: 'Slow Queries', value: db.queryPerformance.slowQueries },
                    { label: 'Error Rate', value: `${db.queryPerformance.errorRate.toFixed(2)}%` }
                ]}
                threshold="Storage: 80%, Connections: 90%, Query Time: 100ms"
                alerts={dbAlerts}
            />
        );
    };

    const StorageHealthCard = () => {
        if (!systemHealth?.storage) return null;

        const storage = systemHealth.storage;

        // Generate storage-specific alerts
        const storageAlerts = [];
        if (storage.totalUsage > 500) {
            storageAlerts.push({
                type: storage.totalUsage > 1000 ? 'critical' : 'warning',
                message: `High storage usage: ${storage.totalUsage.toFixed(1)} GB`
            });
        }
        if (storage.totalCost > 50) {
            storageAlerts.push({
                type: storage.totalCost > 100 ? 'critical' : 'warning',
                message: `High monthly cost: $${storage.totalCost.toFixed(2)}`
            });
        }

        // Determine status based on usage and cost
        let status = 'healthy';
        if (storage.totalUsage > 1000 || storage.totalCost > 100) {
            status = 'critical';
        } else if (storage.totalUsage > 500 || storage.totalCost > 50) {
            status = 'warning';
        }

        return (
            <HealthMetricCard
                title="Storage Usage"
                status={status}
                value={storage.totalUsage.toFixed(1)}
                unit=" GB"
                icon="💾"
                details={[
                    { label: 'Total Cost', value: `$${storage.totalCost.toFixed(2)}/month` },
                    { label: 'Recordings Bucket', value: `${storage.r2Buckets.find(b => b.name.includes('recordings'))?.usage.toFixed(1) || 0} GB` },
                    { label: 'Assets Bucket', value: `${storage.r2Buckets.find(b => b.name.includes('assets'))?.usage.toFixed(1) || 0} GB` },
                    { label: 'Total Files', value: storage.r2Buckets.reduce((sum, bucket) => sum + bucket.fileCount, 0).toLocaleString() }
                ]}
                alerts={storageAlerts}
            />
        );
    };

    const WorkerPerformanceCard = () => {
        if (!systemHealth?.worker) return null;

        const worker = systemHealth.worker;

        // Generate worker-specific alerts
        const workerAlerts = [];
        if (worker.cpuUsage > 70) {
            workerAlerts.push({
                type: worker.cpuUsage > 85 ? 'critical' : 'warning',
                message: `High CPU usage: ${worker.cpuUsage.toFixed(1)}%`
            });
        }
        if (worker.memoryUsage > 75) {
            workerAlerts.push({
                type: worker.memoryUsage > 90 ? 'critical' : 'warning',
                message: `High memory usage: ${worker.memoryUsage.toFixed(1)}%`
            });
        }
        if (worker.errorRate > 1) {
            workerAlerts.push({
                type: worker.errorRate > 5 ? 'critical' : 'warning',
                message: `High error rate: ${worker.errorRate.toFixed(2)}%`
            });
        }
        if (worker.averageExecutionTime > 200) {
            workerAlerts.push({
                type: 'warning',
                message: `Slow execution: ${worker.averageExecutionTime.toFixed(1)}ms avg`
            });
        }

        return (
            <HealthMetricCard
                title="Worker Performance"
                status={worker.status}
                icon="⚡"
                details={[
                    { label: 'Requests/min', value: worker.requestsPerMinute.toLocaleString() },
                    { label: 'Avg Execution Time', value: `${worker.averageExecutionTime.toFixed(1)} ms` },
                    { label: 'Error Rate', value: `${worker.errorRate.toFixed(2)}%` },
                    { label: 'Memory Usage', value: `${worker.memoryUsage.toFixed(1)}%` },
                    { label: 'CPU Usage', value: `${worker.cpuUsage.toFixed(1)}%` }
                ]}
                threshold="Execution: 200ms, Error Rate: 1%, Memory: 80%"
                alerts={workerAlerts}
            />
        );
    };

    const SystemHealthTrends = () => {
        const [trendsData, setTrendsData] = useState(null);
        const [trendsLoading, setTrendsLoading] = useState(false);
        const [trendsTimeRange, setTrendsTimeRange] = useState('24h');

        const fetchTrendsData = async (timeRange = '24h') => {
            try {
                setTrendsLoading(true);
                const token = await getToken();
                const response = await fetch(`https://parrotmate.com/api/admin/system-health/history?timeRange=${timeRange}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setTrendsData(data.history);
                }
            } catch (error) {
                console.error('Failed to fetch trends data:', error);
            } finally {
                setTrendsLoading(false);
            }
        };

        useEffect(() => {
            fetchTrendsData(trendsTimeRange);
        }, [trendsTimeRange]);

        const TrendChart = ({ title, data, dataKey, color = 'teal' }) => {
            if (!data || data.length === 0) {
                return (
                    <div className="h-32 flex items-center justify-center">
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            No trend data available
                        </p>
                    </div>
                );
            }

            const maxValue = Math.max(...data.map(d => d[dataKey] || 0));

            return (
                <div className="space-y-2">
                    <h4 className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {title}
                    </h4>
                    <div className="h-32 flex items-end justify-between space-x-1">
                        {data.slice(-12).map((point, index) => {
                            const height = maxValue > 0 ? ((point[dataKey] || 0) / maxValue) * 100 : 0;
                            return (
                                <div key={index} className="flex flex-col items-center flex-1">
                                    <div className="flex flex-col items-center justify-end h-24 w-full">
                                        <div
                                            className={`bg-${color}-500 rounded-t w-full min-h-1 transition-all duration-300`}
                                            style={{ height: `${height}%` }}
                                            title={`${point[dataKey] || 0} at ${point.timestamp || point.date}`}
                                        ></div>
                                    </div>
                                    <div className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} truncate w-full text-center`}>
                                        {point.timestamp ?
                                            new Date(point.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' }) :
                                            new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                        }
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        };

        return (
            <div className={`${theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
                } rounded-lg shadow border`}>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            System Health Trends
                        </h3>
                        <select
                            value={trendsTimeRange}
                            onChange={(e) => setTrendsTimeRange(e.target.value)}
                            className={`px-3 py-1 rounded border text-sm transition-colors ${theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
                                : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
                                }`}
                        >
                            <option value="1h">Last Hour</option>
                            <option value="24h">Last 24 Hours</option>
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                        </select>
                    </div>
                </div>
                <div className="p-6">
                    {trendsLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
                        </div>
                    ) : trendsData ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <TrendChart
                                title="Database Storage Usage (%)"
                                data={trendsData.data}
                                dataKey="database_storage_usage"
                                color="blue"
                            />
                            <TrendChart
                                title="Worker CPU Usage (%)"
                                data={trendsData.data}
                                dataKey="worker_cpu_usage"
                                color="green"
                            />
                            <TrendChart
                                title="Total Alerts"
                                data={trendsData.data}
                                dataKey="total_alerts"
                                color="red"
                            />
                            <TrendChart
                                title="Storage Usage (GB)"
                                data={trendsData.data}
                                dataKey="storage_total_usage"
                                color="purple"
                            />
                            <TrendChart
                                title="Worker Memory Usage (%)"
                                data={trendsData.data}
                                dataKey="worker_memory_usage"
                                color="yellow"
                            />
                            <TrendChart
                                title="Database Error Rate (%)"
                                data={trendsData.data}
                                dataKey="database_error_rate"
                                color="red"
                            />
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>📈</div>
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                No trend data available
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const SystemAlertsPanel = () => {
        if (!systemHealth?.alerts || systemHealth.alerts.length === 0) {
            return (
                <div className={`${theme === 'dark'
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                    } rounded-lg shadow border p-6`}>
                    <div className="flex items-center space-x-2 mb-4">
                        <span className="text-2xl">🔔</span>
                        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            System Alerts
                        </h3>
                    </div>
                    <div className="text-center py-8">
                        <div className={`text-4xl mb-2 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>✅</div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            No active alerts - all systems operating normally
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div className={`${theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
                } rounded-lg shadow border`}>
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                        <span className="text-2xl">🔔</span>
                        <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            System Alerts ({systemHealth.alerts.length})
                        </h3>
                    </div>
                </div>
                <div className="p-6">
                    <div className="space-y-4">
                        {systemHealth.alerts.map((alert) => (
                            <div key={alert.id} className={`p-4 rounded-lg border ${alert.type === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
                                alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800' :
                                    'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                                }`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${alert.type === 'critical' ? 'bg-red-600 text-white' :
                                                alert.type === 'warning' ? 'bg-yellow-600 text-white' :
                                                    'bg-blue-600 text-white'
                                                }`}>
                                                {alert.type.toUpperCase()}
                                            </span>
                                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {new Date(alert.timestamp).toLocaleString()}
                                            </span>
                                        </div>
                                        <p className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                            {alert.message}
                                        </p>
                                    </div>
                                    {alert.resolved === 0 && (
                                        <button className="ml-4 px-3 py-1 bg-teal-600 text-white text-xs rounded hover:bg-teal-700 transition-colors dark:text-white">
                                            Resolve
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const handleExportHealthData = async (format = 'json', timeRange = '7d') => {
        try {
            const token = await getToken();
            const response = await fetch(`https://parrotmate.com/api/admin/system-health/report?format=${format}&timeRange=${timeRange}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();

                let content, mimeType, filename;

                if (format === 'csv') {
                    content = generateCSVReport(data);
                    mimeType = 'text/csv;charset=utf-8;';
                    filename = `parrotmate-system-health-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
                } else {
                    content = JSON.stringify(data, null, 2);
                    mimeType = 'application/json;charset=utf-8;';
                    filename = `parrotmate-system-health-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
                }

                // Create and download file
                const blob = new Blob([content], { type: mimeType });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                throw new Error('Failed to generate report');
            }
        } catch (error) {
            console.error('Failed to export system health data:', error);
            alert('Failed to export system health data');
        }
    };

    const generateCSVReport = (data) => {
        const lines = [];

        // Header
        lines.push('ParrotMate System Health Report');
        lines.push(`Generated: ${new Date(data.generated_at).toLocaleString()}`);
        lines.push(`Time Range: ${data.time_range}`);
        lines.push(`Current Status: ${data.current_status}`);
        lines.push('');

        // Summary
        lines.push('Summary');
        lines.push('Metric,Value');
        lines.push(`Total Data Points,${data.summary.total_data_points}`);
        lines.push(`Average Uptime,${data.summary.avg_uptime_percentage}%`);
        lines.push(`Total Alerts,${data.summary.total_alerts}`);
        lines.push(`Critical Incidents,${data.summary.critical_incidents}`);
        lines.push('');

        // Historical Data
        if (data.historical_data && data.historical_data.length > 0) {
            lines.push('Historical Data');
            lines.push('Timestamp,Overall Status,Database Status,Storage Status,Worker Status,Total Alerts,Critical Alerts,DB Storage %,DB Error %,Storage GB,Worker CPU %,Worker Memory %');

            data.historical_data.forEach(point => {
                lines.push([
                    point.timestamp || point.date,
                    point.overall_status,
                    point.database_status,
                    point.storage_status,
                    point.worker_status,
                    point.total_alerts || 0,
                    point.critical_alerts || 0,
                    point.metrics?.database_storage_usage || 0,
                    point.metrics?.database_error_rate || 0,
                    point.metrics?.storage_total_usage || 0,
                    point.metrics?.worker_cpu_usage || 0,
                    point.metrics?.worker_memory_usage || 0
                ].join(','));
            });
        }

        return lines.join('\n');
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        System Health Monitoring
                    </h2>
                    <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Real-time infrastructure monitoring and performance metrics
                    </p>
                    {lastUpdated && (
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                            Last updated: {lastUpdated.toLocaleString()}
                        </p>
                    )}
                </div>

                <div className="flex items-center space-x-2">
                    <button
                        onClick={fetchSystemHealth}
                        disabled={loading}
                        className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
                    >
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                        onClick={handleExportHealthData}
                        disabled={loading || !systemHealth}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:text-white"
                    >
                        Export Data
                    </button>
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className={`${theme === 'dark' ? 'bg-red-900 border-red-700 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
                    } border rounded-lg p-4 mb-6`}>
                    <div className="flex items-center">
                        <span className="text-xl mr-2">⚠️</span>
                        <div>
                            <p className="font-medium">Failed to load system health data</p>
                            <p className="text-sm mt-1">{error}</p>
                            <button
                                onClick={fetchSystemHealth}
                                className="text-sm underline mt-2 hover:no-underline"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && !systemHealth && (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-4"></div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Loading system health data...</p>
                    </div>
                </div>
            )}

            {/* Health Metrics Grid */}
            {systemHealth && (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <DatabaseHealthCard />
                        <StorageHealthCard />
                        <WorkerPerformanceCard />
                    </div>

                    {/* System Alerts */}
                    <SystemAlertsPanel />

                    {/* System Health Trends */}
                    <SystemHealthTrends />

                    {/* System Health Export Section */}
                    <div className={`${theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                        } rounded-lg shadow border`}>
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-center">
                                <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    System Health Export & Reporting
                                </h3>
                            </div>
                        </div>
                        <div className="p-6">
                            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-6`}>
                                Export comprehensive system health data including database metrics, storage usage, worker performance, historical trends, and alert history for monitoring and analysis.
                            </p>

                            {/* Export Options */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                        Export Format
                                    </label>
                                    <div className="space-y-2">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="exportFormat"
                                                value="json"
                                                defaultChecked
                                                className="mr-2 text-teal-600 focus:ring-teal-500"
                                            />
                                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                JSON (Detailed data structure)
                                            </span>
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="exportFormat"
                                                value="csv"
                                                className="mr-2 text-teal-600 focus:ring-teal-500"
                                            />
                                            <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                                CSV (Spreadsheet compatible)
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                        Time Range
                                    </label>
                                    <select className={`w-full px-3 py-2 rounded border transition-colors ${theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-teal-500 focus:border-teal-500'
                                        : 'bg-white border-gray-300 text-gray-900 focus:ring-teal-500 focus:border-teal-500'
                                        }`}>
                                        <option value="1h">Last Hour</option>
                                        <option value="24h">Last 24 Hours</option>
                                        <option value="7d" selected>Last 7 Days</option>
                                        <option value="30d">Last 30 Days</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={() => handleExportHealthData('json', '7d')}
                                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 dark:text-white"
                                >
                                    Generate Report
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminSystemHealth;
